import { Box } from "@mui/material";
import { v2 } from "../ui/v2Tokens";
import { SURFACE } from "../ui/primitives";
import type { EnergyBreakdown } from "../../../../utils/nutritionCalculator";
import { energyFigure, energyLabel, energyUnit, type NutritionDisplayMode } from "../ui/nutritionUnits";
import { useNutritionDisplayMode } from "../../../../contexts/NutritionDisplayContext";

/** `2,235`, or `22.4` in points — every figure here is energy */
const fmt = (n: number, mode: NutritionDisplayMode) => (n ? energyFigure(n, mode).text : "—");

const CELLS: Array<{ key: keyof EnergyBreakdown; title: string; abbr: string }> = [
  { key: "bmr", title: "הוצאה במנוחה", abbr: "(BMR)" },
  { key: "neat", title: "פעילות לא יזומה", abbr: "(NEAT)" },
  { key: "eat", title: "הוצאת אימונים", abbr: "(EAT)" },
  { key: "tef", title: "אפקט תרמי", abbr: "(TEF ~10%)" },
];

const TdeeBreakdown = ({ energy }: { energy: EnergyBreakdown }) => {
  const mode = useNutritionDisplayMode();
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "8px" }}>
        {CELLS.map(({ key, title, abbr }) => (
          <Box
            key={key}
            sx={{
              border: `1px solid ${v2.line}`,
              borderRadius: v2.rSm,
              padding: "8px 8px 9px",
              textAlign: "center",
              background: SURFACE,
            }}
          >
            <Box sx={{ fontSize: "11.5px", color: v2.ink, fontWeight: 700, lineHeight: 1.25 }}>{title}</Box>
            <Box sx={{ fontSize: "10.5px", color: v2.ink3, lineHeight: 1.25, marginBottom: "4px" }}>{abbr}</Box>
            <Box sx={{ fontSize: "18px", fontWeight: 800, color: v2.brand, letterSpacing: "-.3px", lineHeight: 1.15 }}>
              {fmt(energy[key], mode)}
            </Box>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "15px 20px",
          borderRadius: "14px",
          background: "rgba(1,187,167,.06)",
          border: "1px solid rgba(1,187,167,.2)",
        }}
      >
        <Box>
          <Box sx={{ fontSize: "15px", fontWeight: 700, color: v2.ink }}>סך הוצאת {energyLabel(mode)} יומית</Box>
          <Box sx={{ fontSize: "12px", color: v2.ink2, marginTop: "1px" }}>
            (TDEE) Total Energy Expenditure
          </Box>
        </Box>
        <Box sx={{ textAlign: "end" }}>
          <Box
            sx={{ fontSize: "30px", fontWeight: 800, lineHeight: 1, letterSpacing: "-1px", color: v2.brand }}
            data-testid="tdee-value"
          >
            {fmt(energy.tdee, mode)}
          </Box>
          <Box sx={{ fontSize: "12px", color: v2.ink2 }}>{energyUnit(mode)} / יום</Box>
        </Box>
      </Box>
    </Box>
  );
};

export default TdeeBreakdown;
